import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { AspectRatios, calculatePlayerDimensions, VideoMetadata } from '@t2c/ai-video-editor';
import { SelectChangeEvent } from '@mui/material/Select/Select';
import { CallbackListener, PlayerRef } from '@remotion/player';
import { VideoPosition } from '../../@types';

type HandlePositionChange = (axis: 'x' | 'y', value: number) => void;

interface PlayerLayoutProps {
  aspectRatio: AspectRatios;
  setAspectRatio: Dispatch<SetStateAction<AspectRatios>>;
  videoMetadata: VideoMetadata;
  playerRef: React.RefObject<PlayerRef>;
  setVideoPosition: Dispatch<SetStateAction<VideoPosition>>;
}

export const usePlayerLayout = ({
  aspectRatio,
  setAspectRatio,
  videoMetadata,
  playerRef,
  setVideoPosition,
}: PlayerLayoutProps) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoVolume, setVideoVolume] = useState(1);
  const [isPlayerFullScreen, setIsPlayerFullScreen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentVideoFrame, setCurrentVideoFrame] = useState(0);
  const [isBuffering, setIsBuffering] = useState(false);

  const playerDimension = useMemo(() => {
    return calculatePlayerDimensions({ videoMetadata, aspectRatio });
  }, [aspectRatio, videoMetadata]);

  const toggleVideoPlayPause = () => {
    if (playerRef.current) {
      if (isVideoPlaying) {
        playerRef.current.pause();
      } else {
        playerRef.current.play();
      }
    }
  };

  const handleVideoVolumeChange = (event: Event, newValue: number | number[]) => {
    if (playerRef.current) {
      const newVolume = newValue as number;
      playerRef.current.setVolume(newVolume);
    }
  };

  const toggleVideoFullScreen = () => {
    if (playerRef.current) {
      if (isPlayerFullScreen) {
        playerRef.current.exitFullscreen();
      } else {
        playerRef.current.requestFullscreen();
      }
    }
  };

  const handleProgressSeek = (event: Event, newValue: number | number[]) => {
    const seekTime = typeof newValue === 'number' ? newValue : newValue[0];
    if (playerRef.current) {
      setCurrentVideoFrame(seekTime);
      playerRef.current.seekTo(seekTime);
    }
  };

  const handleVideoPositionChange: HandlePositionChange = (axis, value) => {
    setVideoPosition((prev) => ({ ...prev, [axis]: value }));
  };

  const handleAspectRatioChange = (event: SelectChangeEvent<string>) => {
    setAspectRatio(event.target.value as AspectRatios);
  };

  const toggleMute = () => {
    if (playerRef.current) {
      const muteState = !isMuted;
      if (muteState) {
        playerRef.current.mute();
      } else {
        playerRef.current.unmute();
      }
      setIsMuted(muteState);
    }
  };

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const onPlay: CallbackListener<'play'> = () => setIsVideoPlaying(true);
    const onVolumeChange: CallbackListener<'volumechange'> = (e) => {
      setVideoVolume(e.detail.volume);
      setIsMuted(e.detail.volume === 0);
    };
    const onPause: CallbackListener<'pause'> = () => setIsVideoPlaying(false);
    const onTimeupdate: CallbackListener<'timeupdate'> = (e) =>
      setCurrentVideoFrame(e.detail.frame);
    const onEnded: CallbackListener<'ended'> = () => console.log('ended');
    const onFullscreenChange: CallbackListener<'fullscreenchange'> = (e) =>
      setIsPlayerFullScreen(e.detail.isFullscreen);
    const onBuffering = () => {
      setIsBuffering(true);
      playerRef.current.mute();
    };
    const onResume = () => {
      setIsBuffering(false);
      if(!isMuted){
        playerRef.current.unmute();
      }
    };

    // Add relevant event listeners
    player.addEventListener('play', onPlay);
    player.addEventListener('volumechange', onVolumeChange);
    player.addEventListener('pause', onPause);
    player.addEventListener('ended', onEnded);
    player.addEventListener('fullscreenchange', onFullscreenChange);
    player.addEventListener('timeupdate', onTimeupdate);
    player.addEventListener('waiting', onBuffering);
    player.addEventListener('resume', onResume);

    return () => {
      // Cleanup listeners on unmount
      player.removeEventListener('play', onPlay);
      player.removeEventListener('volumechange', onVolumeChange);
      player.removeEventListener('pause', onPause);
      player.removeEventListener('ended', onEnded);
      player.removeEventListener('fullscreenchange', onFullscreenChange);
      player.removeEventListener('timeupdate', onTimeupdate);
      player.removeEventListener('waiting', onBuffering);
      player.removeEventListener('resume', onResume);
    };
  }, [playerRef.current]);

  return {
    isVideoPlaying,
    videoVolume,
    isMuted,
    currentVideoFrame,
    isPlayerFullScreen,
    toggleVideoPlayPause,
    handleVideoVolumeChange,
    toggleVideoFullScreen,
    toggleMute,
    handleProgressSeek,
    playerDimension,
    handleAspectRatioChange,
    handleVideoPositionChange,
    isBuffering,
  };
};
